import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetItemsQuery, useDeleteItemMutation, useToggleItemStatusMutation, useToggleItemPremiumMutation, useMassActionMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import DateRangePicker from '../../../components/DateRangePicker';
import Pagination from '../../../components/Pagination';
import { confirmDelete } from '../../../utils/confirm';
import { showSuccess, showError } from '../../../utils/toast';
import { formatDateOnly } from '../../../utils/helpers';

// Format date as "d M, Y" (e.g., "08 Jun, 2024")
const formatDateLaravel = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate().toString().padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

const AdminItems: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [daterange, setDaterange] = useState('');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const { data, isLoading, error, refetch } = useGetItemsQuery({ search, daterange, page });
  const [deleteItem] = useDeleteItemMutation();
  const [toggleItemStatus] = useToggleItemStatusMutation();
  const [toggleItemPremium] = useToggleItemPremiumMutation();
  const [massAction] = useMassActionMutation();

  // Reset to page 1 when search or daterange changes
  useEffect(() => {
    setPage(1);
  }, [search, daterange]);

  const handleDelete = async (id: number) => {
    try {
      const result = await deleteItem(id).unwrap();
      showSuccess(result.message || 'Item deleted successfully!');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to delete item. Please try again.');
    }
  };

  const handleToggleStatus = async (id: number, currentStatus: number) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const result = await toggleItemStatus({ id, status: newStatus }).unwrap();
      showSuccess(result.message || 'Item status updated successfully!');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to update item status. Please try again.');
    }
  };

  const handleTogglePremium = async (id: number, currentPremium: number) => {
    try {
      const newPremium = currentPremium === 1 ? 0 : 1;
      const result = await toggleItemPremium({ id, is_premium: newPremium }).unwrap();
      showSuccess(result.message || 'Premium status updated successfully!');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to update premium status. Please try again.');
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const allIds = items.data.map((item: any) => item.id);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectItem = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(itemId => itemId !== id));
      setSelectAll(false);
    }
  };

  const handleBulkAction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bulkAction || selectedIds.length === 0) {
      showError('Please select an action and at least one item');
      return;
    }

    if (bulkAction === 'delete') {
      const confirmed = await confirmDelete('Are you sure?', "You won't be able to revert this!");
      if (!confirmed) return;
    }

    try {
      const result = await massAction({
        type: 'item',
        action: bulkAction,
        selectedIds: selectedIds.join(','),
      }).unwrap();
      showSuccess(result.message || 'Bulk action completed successfully!');
      setSelectedIds([]);
      setSelectAll(false);
      setBulkAction('');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to perform bulk action. Please try again.');
    }
  };

  const handleExport = () => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (daterange) params.append('daterange', daterange);
    params.append('export', '1');
    window.open(`/admin/items?${params.toString()}`, '_blank');
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return <div className="alert alert-danger m-5">Error loading items</div>;
  }

  const items = data?.data || { data: [], current_page: 1, last_page: 1, total: 0, per_page: 15 };

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Items</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">Items</li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <a href="#" className="btn btn-sm btn-light-primary me-3" onClick={handleExport}>
                <span className="svg-icon svg-icon-2">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect opacity="0.3" x="12.75" y="4.25" width="12" height="2" rx="1" transform="rotate(90 12.75 4.25)" fill="currentColor"></rect>
                    <path d="M12.0573 6.11875L13.5203 7.87435C13.9121 8.34457 14.6232 8.37683 15.056 7.94401C15.4457 7.5543 15.4641 6.92836 15.0979 6.51643L12.4974 3.59084C12.0996 3.14332 11.4004 3.14332 11.0026 3.59084L8.40206 6.51643C8.0359 6.92836 8.0543 7.5543 8.44401 7.94401C8.87683 8.37683 9.58785 8.34458 9.9797 7.87435L11.4427 6.11875C11.6026 5.92684 11.8974 5.92684 12.0573 6.11875Z" fill="currentColor"></path>
                    <path opacity="0.3" d="M18.75 8.25H17.75C17.1977 8.25 16.75 8.69772 16.75 9.25C16.75 9.80228 17.1977 10.25 17.75 10.25C18.3023 10.25 18.75 10.6977 18.75 11.25V18.25C18.75 18.8023 18.3023 19.25 17.75 19.25H5.75C5.19772 19.25 4.75 18.8023 4.75 18.25V11.25C4.75 10.6977 5.19771 10.25 5.75 10.25C6.30229 10.25 6.75 9.80228 6.75 9.25C6.75 8.69772 6.30229 8.25 5.75 8.25H4.75C3.64543 8.25 2.75 9.14543 2.75 10.25V19.25C2.75 20.3546 3.64543 21.25 4.75 21.25H18.75C19.8546 21.25 20.75 20.3546 20.75 19.25V10.25C20.75 9.14543 19.8546 8.25 18.75 8.25Z" fill="currentColor"></path>
                  </svg>
                </span>
                Export
              </a>
              <a href="#" className="btn btn-base btn-sm" onClick={(e) => { e.preventDefault(); navigate('/admin/items/add'); }}>
                Add New Item
              </a>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-header border-0 pt-6">
                <div className="card-title">
                  <form onSubmit={(e) => { e.preventDefault(); }}>
                    <div className="d-flex align-items-center position-relative my-1">
                      <span className="svg-icon svg-icon-1 position-absolute ms-6">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor"></rect>
                          <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor"></path>
                        </svg>
                      </span>
                      <input
                        type="text"
                        className="form-control form-control-solid w-350px ps-15 me-5"
                        placeholder="Search Package Name.."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      <div className="position-relative d-flex align-items-center">
                        <span className="svg-icon svg-icon-2 position-absolute mx-4" style={{ zIndex: 1 }}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path opacity="0.3" d="M21 22H3C2.4 22 2 21.6 2 21V5C2 4.4 2.4 4 3 4H21C21.6 4 22 4.4 22 5V21C22 21.6 21.6 22 21 22Z" fill="currentColor"></path>
                            <path d="M6 6C5.4 6 5 5.6 5 5V3C5 2.4 5.4 2 6 2C6.6 2 7 2.4 7 3V5C7 5.6 6.6 6 6 6ZM11 5V3C11 2.4 10.6 2 10 2C9.4 2 9 2.4 9 3V5C9 5.6 9.4 6 10 6C10.6 6 11 5.6 11 5ZM15 5V3C15 2.4 14.6 2 14 2C13.4 2 13 2.4 13 3V5C13 5.6 13.4 6 14 6C14.6 6 15 5.6 15 5Z" fill="currentColor"></path>
                          </svg>
                        </span>
                        <DateRangePicker
                          value={daterange}
                          onChange={setDaterange}
                          placeholder="Date Range"
                        />
                        <button type="submit" className="btn w-120px btn-base mx-3">Filter</button>
                        {(search || daterange) && (
                          <a href="#" onClick={(e) => { e.preventDefault(); setSearch(''); setDaterange(''); }} className="btn btn-sm btn-light-primary me-3">
                            Clear
                          </a>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
              </div>
              <div className="card-body pt-0">
                <form onSubmit={handleBulkAction} className="d-flex mb-3">
                  <select
                    name="action"
                    required
                    className="form-control w-80px p-2 me-2"
                    value={bulkAction}
                    onChange={(e) => setBulkAction(e.target.value)}
                  >
                    <option value="">Action</option>
                    <option value="delete">Delete</option>
                  </select>
                  <button className="btn btn-base btn-sm p-3" type="submit">Confirm</button>
                </form>
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th className="w-10px pe-2">
                          <input
                            className="form-check-input border-base mt-2"
                            type="checkbox"
                            id="checkAll"
                            checked={selectAll}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th>#</th>
                        <th>Image</th>
                        <th>Name</th>
                        <th>Name Ar</th>
                        <th>Category</th>
                        <th>Description</th>
                        <th>Status</th>
                        <th>Is Premium</th>
                        <th>Created Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600">
                      {items.data.map((item: any, index: number) => (
                        <tr key={item.id}>
                          <td>
                            <input
                              className="form-check-input border-base mt-2 action_ids"
                              type="checkbox"
                              value={item.id}
                              checked={selectedIds.includes(item.id)}
                              onChange={(e) => handleSelectItem(item.id, e.target.checked)}
                            />
                          </td>
                          <td>{(items.current_page - 1) * 15 + index + 1}</td>
                          <td><img src={item.images?.[0] || '/media/blank.png'} width="60" className="rounded" alt="" /></td>
                          <td>{item.name}</td>
                          <td>{item.name_ar || '-'}</td>
                          <td>{item.category?.name || item.category?.title || 'N/A'}</td>
                          <td>{item.description ? `${item.description.substring(0, 100)}...` : '-'}</td>
                          <td>
                            <button
                              className="activeInactive p-0 btn btn-transparent"
                              onClick={() => handleToggleStatus(item.id, item.status)}
                            >
                              <span className={`badge badge-light-${item.status === 1 ? 'success' : 'danger'}`}>
                                {item.status === 1 ? 'Active' : 'Inactive'}
                              </span>
                            </button>
                          </td>
                          <td>
                            <div className="form-check form-switch mt-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={item.is_premium === 1}
                                onChange={() => handleTogglePremium(item.id, item.is_premium)}
                              />
                            </div>
                          </td>
                          <td>{formatDateLaravel(item.created_at)}</td>
                          <td className="text-center d-flex mt-2">
                            <a
                              href="#"
                              className="btn btn-sm h-30px btn-light-primary me-1"
                              onClick={(e) => { e.preventDefault(); navigate(`/admin/items/${item.id}/edit`); }}
                            >
                              <i className="fas fa-pencil"></i>
                            </a>
                            <a
                              href="#"
                              className="btn btn-sm h-30px btn-light-primary me-1"
                              onClick={(e) => { e.preventDefault(); navigate(`/admin/items/${item.id}`); }}
                            >
                              <i className="fas fa-eye"></i>
                            </a>
                            <button
                              type="button"
                              className="delBtn btn btn-sm btn-light-primary"
                              onClick={() => handleDelete(item.id)}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {items.data.length > 0 && (
                  <div className="card-footer border-0 pt-6">
                    <Pagination
                      currentPage={items.current_page}
                      lastPage={items.last_page}
                      total={items.total || 0}
                      perPage={items.per_page || 15}
                      onPageChange={(newPage) => setPage(newPage)}
                      showTotal={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminItems;
